import React, { useEffect, useState } from "react";
import {
  Alert
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { BASE_URL } from '../constants';

const Login = () => {
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { login, user } = useAuth();

  // Google OAuth client ID, sourced from Secret Manager at build time via
  // frontend/cloudbuild.yaml (secret: ada-compliance-google-client-id). There is
  // deliberately no fallback — a misconfigured build must fail visibly rather
  // than quietly authenticating against another environment's OAuth client.
  // The client's "Authorized JavaScript origins" must include this site's origin.
  const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  useEffect(() => {
    if (!CLIENT_ID) {
      setError(
        'Sign-in is unavailable: this build is missing REACT_APP_GOOGLE_CLIENT_ID. ' +
        'Rebuild via frontend/cloudbuild.yaml so the value is read from Secret Manager.'
      );
    }

    // Redirect if user is already logged in
    if (user) {
      navigate('/upload');
    }

    // Load Google API Script
    const loadGoogleScript = () => {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);

      return script;
    };

    const script = loadGoogleScript();

    return () => {
      // Cleanup script on unmount
      document.body.removeChild(script);
    };
  }, [user, navigate, CLIENT_ID]);

  const handleGoogleLogin = () => {
    if (!CLIENT_ID) {
      setError(
        'Sign-in is unavailable: this build is missing REACT_APP_GOOGLE_CLIENT_ID. ' +
        'Rebuild via frontend/cloudbuild.yaml so the value is read from Secret Manager.'
      );
      return;
    }
    try {
      const google = window.google;
      const client = google.accounts.oauth2.initTokenClient({
        client_id: CLIENT_ID,
        scope: 'https://www.googleapis.com/auth/userinfo.profile https://www.googleapis.com/auth/userinfo.email',
        callback: async (response) => {
          if (response.access_token) {
            // Fetch user data with the access token
            try {
              const userInfoResponse = await fetch(
                'https://www.googleapis.com/oauth2/v3/userinfo',
                {
                  headers: {
                    Authorization: `Bearer ${response.access_token}`,
                  },
                }
              );

              if (!userInfoResponse.ok) {
                throw new Error('Failed to get user info');
              }

              const userInfo = await userInfoResponse.json();

              // Enforce Marketplace entitlement: only users whose organization
              // has an active Google Cloud Marketplace purchase may access the
              // app. One purchase entitles the whole work domain.
              const accessRes = await fetch(`${BASE_URL}/api/marketplace/check-access`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  email: userInfo.email,
                  hosted_domain: userInfo.hd || '',
                  google_user_id: userInfo.sub || '',
                }),
              });
              const access = await accessRes.json().catch(() => ({ has_access: false }));
              if (!access.has_access) {
                setError(
                  "This Google account doesn't have access to ADA Compliance. Your " +
                  "organization needs an active Google Cloud Marketplace purchase: the " +
                  "purchaser registers it from their Marketplace order, and colleagues " +
                  "then sign in with their work account."
                );
                return;
              }

              // Store user data and token
              login({
                ...userInfo,
                access_token: response.access_token
              });

              navigate('/upload');
            } catch (error) {
              console.error('Error fetching user info:', error);
              setError('Failed to get user information. Please try again.');
            }
          }
        },
        error_callback: (error) => {
          console.error('Google OAuth error:', error);
          setError('Authentication failed. Please try again.');
        },
      });

      client.requestAccessToken();
    } catch (error) {
      console.error('Error during Google sign in:', error);
      setError('An error occurred during login. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center items-center p-4">
      {/* Login Card */}
      <div className="w-full max-w-md">
        {/* Title */}
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-semibold text-gray-900">
            ADA Compliance
          </h1>
        </div>

        {/* Card Content */}
        <div className="bg-white py-8 px-4 shadow-sm rounded-lg sm:px-10">
          <div className="space-y-6">
            {error && (
              <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                {error}
              </Alert>
            )}
            {/* Google Sign In Button */}
            <button
              className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 shadow-sm text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              onClick={handleGoogleLogin}
            >
              <svg
                className="w-5 h-5 mr-3"
                viewBox="0 0 24 24"
              >
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              Sign in with Google
            </button>

            {/* Sign up link */}
            <div className="text-sm text-center text-gray-600">
              New here?{" "}
              <button
                type="button"
                onClick={() => navigate('/signup')}
                className="text-blue-600 hover:text-blue-700 font-medium"
              >
                Create an account
              </button>
            </div>

            {/* Footer Text */}
            <div className="text-sm text-center text-gray-500 mt-8">
              By signing in, you agree to our Terms of Service and Privacy Policy
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Login;